import { Link, useParams } from 'react-router-dom';
import { AsyncBoundary, Card, Icon, type IconName } from '@kollez/design-system';
import {
  useApplication,
  useApplicationTimeline,
  useApplicationRun,
} from '@/lib/queries';
import { formatIndianDate, formatLPA, formatTime } from '@kollez/shared';

/**
 * /applications/:id — student's permalink to one application.
 *
 * Consumes 5a-5aa schema:
 *   - Application (5d funnel) via useApplication → header + status + drive/opportunity
 *   - Application timeline (Pass 10 relationship endpoint) → statusHistory + interviews +
 *     offers + earned Badges + WorkflowRun stages + attempts merged in chronological order
 *   - WorkflowRun by-application (5e) → current stage + auto-satisfied + full history
 *
 * The /applications list already shows a drawer with the same data, but the drawer's
 * 'Open detail' link previously 404'd. This page fills that route.
 */

function TimelineIcon({ kind }: { kind: string }) {
  const map: Record<string, IconName> = {
    status: 'chevron-right',
    interview: 'user-check',
    offer: 'file-signature',
    badge: 'calendar-plus',
    workflow: 'chevron-right',
    attempt: 'clipboard-check',
    created: 'chevron-right',
  };
  const name = (map[kind] ?? 'chevron-right') as IconName;
  return <Icon name={name} size={12} />;
}

function statusTone(s: string): string {
  if (/^offer|accepted|selected/i.test(s)) return 'badge-success';
  if (/reject|withdraw|voided|cancel/i.test(s)) return 'badge-danger';
  if (/interview|shortlist/i.test(s)) return 'badge-info';
  return '';
}

export function StudentApplicationDetailPage() {
  const { id = '' } = useParams();
  const appQ = useApplication(id);
  const timeline = useApplicationTimeline(id);
  const runQ = useApplicationRun(id);
  const application = appQ.data?.application;
  const drive = application?.drive;
  const opp = drive?.opportunity ?? drive?.roles?.[0]?.opportunity;
  const run = runQ.data?.run;

  return (
    <AsyncBoundary
      kind="detail"
      loading={appQ.isLoading}
      error={appQ.error}
      empty={!appQ.isLoading && !application}
      emptyTitle="Application not found"
      emptyHint="It may have been withdrawn or a stale link."
      onRetry={() => void appQ.refetch()}
    >
      {application && (
        <div className="stack">
          <div className="page-header">
            <div className="ph-text">
              <p className="eyebrow">
                <Link to="/applications">My applications</Link> · {drive?.partner?.name ?? 'Drive'}
              </p>
              <h1 className="h-page">{opp?.title ?? drive?.driveName ?? 'Application'}</h1>
              <p className="subhead">
                <span className={`badge ${statusTone(application.status)}`}>{application.status.replace(/_/g, ' ')}</span>
                {drive?.driveDate && <> · Drive on {formatIndianDate(new Date(drive.driveDate))}</>}
                {opp?.compensation?.ctc?.min && <> · From {formatLPA(opp.compensation.ctc.min)}</>}
              </p>
            </div>
          </div>

          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px] items-start">
            <div className="stack">
              {application.matchScore && (
                <Card padding="md">
                  <h2 className="h-section">Match score</h2>
                  <p className="cell-sub m-0">
                    <b>{application.matchScore.overall}%</b> overall.
                    {application.matchScore.reasoning && <> {application.matchScore.reasoning}</>}
                  </p>
                </Card>
              )}

              <Card padding="md">
                <h2 className="h-section">Timeline</h2>
                <AsyncBoundary
                  kind="detail"
                  loading={timeline.isLoading}
                  error={timeline.error}
                  empty={!timeline.isLoading && (timeline.data?.events?.length ?? 0) === 0}
                  emptyTitle="No events yet"
                >
                  <ol className="timeline">
                    {(timeline.data?.events ?? [])
                      .slice()
                      .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
                      .map((ev, i) => (
                        <li key={`${ev.kind}-${ev.at}-${i}`} data-state={i === 0 ? 'current' : 'done'}>
                          <span className="t-dot" />
                          <div className="t-title">
                            <TimelineIcon kind={ev.kind} /> {ev.title}
                          </div>
                          <div className="t-meta">
                            {formatIndianDate(new Date(ev.at))} {formatTime(ev.at)}
                            {ev.detail ? ` · ${ev.detail}` : ''}
                          </div>
                        </li>
                      ))}
                  </ol>
                </AsyncBoundary>
              </Card>
            </div>

            <aside className="stack">
              {run && (
                <Card padding="md">
                  <h2 className="h-section">Workflow</h2>
                  <dl className="dl">
                    <dt>Current stage</dt><dd><span className="badge badge-info">{run.currentStage}</span></dd>
                    <dt>Since</dt><dd className="cell-sub">{formatIndianDate(new Date(run.enteredCurrentStageAt))}</dd>
                    <dt>Status</dt><dd>{run.status}</dd>
                    {run.autoSatisfiedStages.length > 0 && (<>
                      <dt>Auto-satisfied</dt>
                      <dd className="cell-sub">{run.autoSatisfiedStages.join(', ')}</dd>
                    </>)}
                  </dl>
                  {run.history.length > 0 && (
                    <div className="mt-3">
                      <div className="cell-sub mb-1">Transitions ({run.history.length})</div>
                      <ol className="timeline">
                        {run.history.slice().reverse().map((h, i) => {
                          const emitted = typeof h.emittedBadge === 'object' ? h.emittedBadge : null;
                          const satisfied = typeof h.satisfiedByBadge === 'object' ? h.satisfiedByBadge : null;
                          return (
                            <li key={i} data-state="done">
                              <span className="t-dot" />
                              <div className="t-title">
                                {h.fromStage} → {h.toStage}
                                {h.auto && <span className="badge ml-1">auto</span>}
                              </div>
                              <div className="t-meta">
                                {formatIndianDate(new Date(h.at))}
                                {h.by?.fullName && ` · ${h.by.fullName}`}
                                {satisfied && (
                                  <> · satisfied by <Link to={`/profile/badges/${satisfied._id}`}>{satisfied.skill ?? 'badge'}</Link></>
                                )}
                                {emitted && (
                                  <> · earned <Link to={`/profile/badges/${emitted._id}`}>{emitted.skill ?? 'badge'}</Link></>
                                )}
                                {h.note && ` · ${h.note}`}
                              </div>
                            </li>
                          );
                        })}
                      </ol>
                    </div>
                  )}
                </Card>
              )}

              <Card padding="md">
                <h2 className="h-section">Drive</h2>
                <dl className="dl">
                  <dt>Company</dt><dd>{drive?.partner?.name ?? '—'}</dd>
                  {typeof drive?.tier === 'number' && (<>
                    <dt>Tier</dt>
                    <dd>Day {drive.tier}</dd>
                  </>)}
                  {opp?.eligibility?.minCGPA && (<>
                    <dt>Min CGPA</dt><dd>{opp.eligibility.minCGPA}</dd>
                  </>)}
                  {opp?.eligibility?.maxBacklogs !== undefined && (<>
                    <dt>Max backlogs</dt><dd>{opp.eligibility.maxBacklogs}</dd>
                  </>)}
                  {(opp?.eligibility?.branches?.length ?? 0) > 0 && (<>
                    <dt>Branches</dt>
                    <dd className="cell-sub">{opp!.eligibility!.branches!.join(', ')}</dd>
                  </>)}
                </dl>
              </Card>
            </aside>
          </div>
        </div>
      )}
    </AsyncBoundary>
  );
}
